// Protocol/IB source field mappings for RAG-based context fetching
// Maps ICF sections to Protocol/IB sections that should be used as context
// Includes both main sections and subsections (subsections will be extracted from parent sections if not found separately)
const PROTOCOL_SOURCE_MAPPINGS = {
  '1. Key information': [
    { type: 'PROTOCOL', titles: ['2.2 Assessment of Risks and Benefits','1. PROTOCOL SUMMARY', '1.2 TRIAL SCHEMA', '1.3 SCHEDULE OF ACTIVITIES', '4. TRIAL DESIGN','4.1 Description of Trial Design','5.1 Description of Trial Population and Rationale','5.2 Inclusion Criteria','5.3 Exclusion Criteria','6. TRIAL INTERVENTION AND CONCOMITANT THERAPY','6.7 Investigational Trial Intervention Assignment, Randomization, and Blinding','5. TRIAL POPULATION', '2. INTRODUCTION', '7. PARTICIPANT DISCONTINUATION OF TRIAL INTERVENTION AND DISCONTINUATION OR WITHDRAWAL FROM TRIAL','7.2 Participant Discontinuation or Withdrawal from the Trial'] }
  ],
  '2. About this study & why invited': [
    { type: 'PROTOCOL', titles: ['2. INTRODUCTION', '2.1 Purpose of Trial', '3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS','3.1 Primary Objective(s) and Associated Estimand(s)','3.2 Secondary Objective(s) and Associated Estimand(s)','5. TRIAL POPULATION','3.3 Exploratory Objective(s)','5.1 Description of Trial Population and Rationale','5.2 Inclusion Criteria','5.3 Exclusion Criteria','4.1 Description of Trial Design','10.11 Sample Size Determination','10. STATISTICAL CONSIDERATIONS','4. TRIAL DESIGN'] }
  ],
  '3. What will happen if you join': [
    { type: 'PROTOCOL', titles: ['1.3 SCHEDULE OF ACTIVITIES', '8. TRIAL ASSESSMENTS AND PROCEDURES'] }
  ],
  '4. Which treatment will you get?': [
    { type: 'PROTOCOL', titles: ['6. TRIAL INTERVENTION AND CONCOMITANT THERAPY', '6.7 Investigational Trial Intervention Assignment, Randomization, and Blinding', '4. TRIAL DESIGN','4.1 Description of Trial Design'] }
  ],
  '5. Risks and discomforts': [
    { type: 'PROTOCOL', titles: ['2. INTRODUCTION', '2.2 Assessment of Risks and Benefits', '8. TRIAL ASSESSMENTS AND PROCEDURES'] }
  ],
  '6. Possible benefits': [
    { type: 'PROTOCOL', titles: ['2. INTRODUCTION', '2.2 Assessment of Risks and Benefits','3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS','3.1 Primary Objective(s) and Associated Estimand(s)','3.2 Secondary Objective(s) and Associated Estimand(s)'] }
  ],
  '7. Your choices (alternatives)': [
    { type: 'PROTOCOL', titles: ['4.1 Description of Trial Design','1. PROTOCOL SUMMARY'] }
  ],
  '8. Privacy & use of your information': [
    { type: 'PROTOCOL', titles: ['11. TRIAL OVERSIGHT AND OTHER GENERAL CONSIDERATIONS', '11.7 Data Governance', '11.8 Data Protection', '11.3 Informed Consent Process'] }
  ],
  '9. Costs, payments, injury care': [
    { type: 'PROTOCOL', titles: ['11. TRIAL OVERSIGHT AND OTHER GENERAL CONSIDERATIONS', '11.5 Insurance and Indemnity'] }
  ],
  '10. Pregnancy & contraception (if applicable)': [
    { type: 'PROTOCOL', titles: ['9.3 Pregnancy and Postpartum Information','5.4 Contraception'] }
  ],
  '11. Samples & future research (if applicable)': [
    { type: 'PROTOCOL', titles: ['8.6 Biomarkers','1.3 SCHEDULE OF ACTIVITIES','8.7 Immunogenicity Assessments'] }
  ],
  '12. If you stop/leave; post‑trial access': [
    { type: 'PROTOCOL', titles: ['7.1 Discontinuation of Trial Intervention for Individual Participants','7.2 Participant Discontinuation or Withdrawal from the Trial','1.3 SCHEDULE OF ACTIVITIES'] }
  ],
  '13. Oversight, contacts, results, eConsent': [
    { type: 'PROTOCOL', titles: ['11.4 Committees', '11.3 Informed Consent Process'] }
  ],
  'Consent & signatures': [
    { type: 'PROTOCOL', titles: ['11.3 Informed Consent Process'] }
  ]
};

// Default ICF section templates
const DEFAULT_ICF_SECTIONS = [
  { 
    title: '1. Key information',
    protocolSources: PROTOCOL_SOURCE_MAPPINGS['1. Key information'],
    prompt: `You are an expert clinical-regulatory medical writer drafting an Informed Consent Form (ICF) for a global oncology clinical trial. Write only Section 1: Key Information.

Requirements
Use clear, patient-friendly language suitable for approximately an 8th-grade reading level.
Use a question-and-answer format.
Write directly to the participant using "you" and "your."
Keep the section concise (approximately 1–2 pages).
Use short sentences, short paragraphs, and simple words whenever possible.
Avoid legal, promotional, scientific, or technical language unless necessary; explain any unavoidable medical terms in plain language.
Do not duplicate information that will be described in later sections.
Present balanced information about possible risks and benefits.
Clearly state that participation is voluntary.
Do not use wording such as "No, you do not have to be in this study."
Instead, use clear language such as: "Participation in this study is voluntary."
Avoid detailed procedural descriptions; provide only high-level summaries.
Use bullet points where appropriate.

Study Information
[Insert protocol-specific information]

Include the following questions and answers:
What is this study about?
Describe:
The disease or condition being studied.
The purpose of the study.
Whether the study treatment is investigational.

Why might I want to participate?
Describe:
Possible benefits, if any.
How the study may help future patients.
That benefits cannot be guaranteed.

What are the main risks?
Provide:
A brief summary of important known risks.
A statement that there may be risks that are not yet known.

Is participation voluntary?
Explain:
Participation in the study is voluntary.
Choosing not to participate will not affect the participant's regular medical care, benefits, or legal rights.
Participants may decide to leave the study at any time.

How long will participation last?
Provide:
Approximate length of participation.
A statement that more details are provided later in the consent form.

Style Instructions

Use the headings exactly as listed above.
Keep answers brief, clear, and easy to understand.
Maintain a neutral and balanced tone.
Do not include detailed procedures, privacy information, costs, injury information, contacts, or signature language.`, 
    context: '' 
  },
  { 
    title: '2. About this study and Why You Are Being Invited to Participate',
    protocolSources: PROTOCOL_SOURCE_MAPPINGS['2. About this study & why invited'],
    prompt: `You are an expert clinical-regulatory medical writer drafting an Informed Consent Form (ICF) for a global oncology clinical trial. Write only Section 2: About This Study and Why You Are Invited.

Requirements
Use plain-language, patient-focused wording.
Use a question-and-answer format.
Write in second person ("you").
Avoid scientific jargon whenever possible.
Ensure compliance with FDA informed consent requirements, ICH E6(R3), and international ethics committee expectations.
Do not repeat information already covered in Section 1 except where necessary for understanding.
Do not include risks, benefits, privacy information, costs, injury compensation, or consent language.

Study Information
[Insert protocol-specific information]
Include the following questions and answers:
Why is this study being done?
Describe:
Scientific purpose of the study.
Primary objectives.
What researchers hope to learn.

Why have I been invited?
Describe:
Disease or condition.
Key eligibility rationale.
Why the participant may qualify.

Is this research?
Clearly explain:
This is a research study.
The treatment or intervention may be investigational.
Researchers are collecting information to answer scientific questions.
Standard medical care and research are different.

How many people will take part?
Describe:
Planned number of participants.
Countries and/or study sites involved (if applicable).

Style Instructions
Use the exact question headings listed above.
Keep explanations brief, clear, and understandable to a non-medical audience.
Use short paragraphs.
Maintain a neutral and balanced tone.
Do not discuss study procedures in detail; those belong in a later section.
Avoid repeating detailed information from Section 1.`, 
    context: '' 
  },
  { 
    title: '3. What Will Happen If You Join This Study?',
    protocolSources: PROTOCOL_SOURCE_MAPPINGS['3. What will happen if you join'],
    prompt: `You are an expert clinical-regulatory medical writer drafting an Informed Consent Form (ICF) for a global oncology clinical trial. Write only Section 3: What Will Happen If You Join?


Requirements

Use clear, patient-friendly language suitable for approximately an 8th-grade reading level.
Use a question-and-answer format.
Write directly to the participant using "you" and "your."
Use short sentences, plain language, and short paragraphs.
Avoid unnecessary medical, scientific, legal, or regulatory jargon.
If a medical term must be used, explain it in simple language.
Ensure the content aligns with FDA informed consent requirements, ICH E6(R3), and international ethics committee expectations.
Describe what participation involves from screening through follow-up.
Focus on what participants will experience and what they will be asked to do.
Present information in the order participants are likely to experience it.
Clearly distinguish between:
Screening activities
Study participation activities
End-of-treatment activities
Follow-up activities


Use bullet points where appropriate to improve readability.
Maintain a neutral, factual, and non-promotional tone.
Use only information provided in the source materials.
Do not invent procedures, assessments, visits, timelines, questionnaires, sample collections, or participant activities.
Do not discuss detailed risks, benefits, privacy, costs, compensation, injury care, alternatives, or signatures, as these are covered elsewhere in the consent form.
This section serves as the primary location within the ICF for describing study procedures, assessments, participant activities, sample collections, questionnaires, and follow-up requirements.


Source Documents
Use only the following source materials:

Schedule of Activities (SoA) tables
Protocol Section 8 (Study Procedures/Study Assessments), including all subsections

These sources should be treated as the primary source of truth for participant-facing study activities.

Procedure and Activity Extraction Requirements
Before drafting this section:
Review the SoA and Protocol Section 8 and identify all participant-facing activities.
Capture all activities described in these sources, including activities that are:


Mandatory
Optional
Cohort-specific
Arm-specific
Phase-specific
Country-specific

One-time
Repeated
Exploratory

Do not omit an activity simply because it applies only to a subset of participants.

Activities That Must Be Evaluated for Inclusion
Review the SoA and Protocol Section 8 for any of the following:

Screening Activities
Eligibility assessments
Medical history review
Medication review
Physical examinations
Vital signs
ECOG/performance status assessments
Pregnancy testing
Laboratory testing
ECGs
Imaging assessments
Tumor tissue review
Biomarker assessments
Genetic testing
Any other screening procedures


Treatment Activities
Study treatment administration
Infusions
Injections
Oral medications
Device-related procedures
Combination therapies


Clinical and Safety Assessments
Physical examinations
Vital signs
Disease assessments
Safety monitoring
Adverse event assessments
Concomitant medication reviews

Laboratory and Sample Collection Activities
Blood samples
Urine samples
Saliva samples
Stool samples
Tissue samples
Bone marrow samples
Buccal swabs
Cerebrospinal fluid samples
Any other biological sample collection
Tumor and Biomarker Activities
Archival tissue collection
Fresh tissue collection

Mandatory biopsies
Optional biopsies
Biomarker testing
Molecular testing
Genetic/genomic testing
Companion diagnostic testing

Imaging and Diagnostic Procedures
CT scans
MRI scans
PET scans
Bone scans
X-rays
Ultrasounds
Other imaging assessments

Questionnaires and Participant-Reported Activities
Quality-of-life questionnaires
Symptom questionnaires
Patient-reported outcomes (PROs)
Health surveys
Cognitive assessments
Electronic questionnaires
Caregiver questionnaires

Digital and Remote Activities
Electronic diaries
Mobile applications
Wearable devices
Telehealth visits
Remote monitoring activities

Optional Activities
Optional biopsies
Optional sample collection
Optional genetic testing
Optional questionnaires
Optional imaging procedures
Optional research activities
Optional sub-studies


Follow-Up Activities
End-of-treatment assessments
Safety follow-up
Long-term follow-up
Survival follow-up
Collection of information about future cancer treatments
Disease status assessments

Mandatory Inclusion Rules
For every participant-facing activity identified:

Include it somewhere within Section 3.
Clearly indicate whether the activity is:
Required for all participants,
Required only for certain participants, or
Optional.


For optional activities, use participant-friendly language such as:

"You may choose whether or not to take part in this optional activity."
Explain the purpose of major procedures in plain language whenever helpful.

Include:
Assessments
Procedures
Sample collections
Imaging procedures
Biopsies
Biomarker testing
Genetic testing
Questionnaires
PROs
Diaries
Apps
Wearables
Follow-up activities


Do not omit an activity because it:
Occurs only once,
Occurs at selected visits,
Applies only to some participants,
Is exploratory,
Requires separate optional consent.
If an activity requires separate optional consent, clearly state that participation in that activity is optional.


Study Information
[Insert protocol-specific information]


Include the Following Questions and Answers
What happens before I can join the study?
Describe:


Screening period.
Eligibility assessments.
Screening procedures.
Any required tissue, biomarker, genetic, laboratory, imaging, or pregnancy-related assessments used to determine eligibility.
Explain that screening tests are performed to determine whether the participant qualifies for the study.
State that not everyone who signs the consent form will qualify for participation.


What will happen if I join the study?
Provide a participant-friendly overview of:
Study visits.
Treatment administration.
Assessments.
Sample collection activities.
Imaging procedures.
Questionnaires.
Electronic reporting tools.
Digital activities.
Optional research activities.
Any other participant-facing procedures identified in the source documents.
Requirements:
Cover both required and optional activities.
Group similar activities together where appropriate.
Avoid reproducing visit schedules or protocol tables.
Clearly distinguish optional activities from required activities.
At the end of the answer, include a brief statement that additional information about study treatment is provided later in the consent form.


What will I be expected to do during the study?
Describe participant responsibilities, such as:
Attending study visits.
Receiving study treatment.
Taking study medication as instructed, if applicable.
Reporting side effects or changes in health.
Completing questionnaires, diaries, electronic reports, or app-based activities, if applicable.
Participating in required assessments.
Following study instructions.
Following pregnancy prevention requirements, if applicable.
Focus on practical expectations.

How long will I be in the study?
Describe:
Screening period.
Treatment period.
Follow-up period.
Total expected duration of participation.
Requirements:

Use the duration provided in the source materials.
If duration may differ among participants, explain why.
Do not estimate timelines that are not stated in the source documents.
At the end of the answer, include a brief statement that participation may end earlier under certain circumstances, which are explained later in the consent form.


What happens when my study treatment ends?
Describe:


End-of-treatment visit(s).
Final assessments.

Safety assessments.

Sample collections, if applicable.

Other end-of-treatment procedures described in the source materials.

Explain what participants should generally expect after treatment stops.


Will I be followed after my treatment ends?
Describe:


Follow-up visits.

Follow-up calls.

Long-term follow-up.

Survival follow-up.

Future treatment information collection.

Disease status assessments.

Any other post-treatment activities described in the source materials.

Explain the purpose of follow-up in simple language.


Style Instructions

Use the question headings exactly as written above.

Keep explanations concise, clear, and easy to understand.

Use short paragraphs and bullet points where appropriate.

Present activities in the order participants are likely to experience them.

Focus on what participants need to know rather than protocol operations.

Avoid duplicating treatment assignment information that belongs in the treatment section.

Avoid discussing detailed risks, benefits, privacy, costs, compensation, injury care, or alternatives.

Integrate cross-references naturally into the text rather than using standalone section references.

Ensure participants can clearly understand:

What screening involves,

What happens during participation,

What they will be expected to do,

What optional activities are available,

How long participation may last, and

What happens after treatment ends.
`, 
    context: '' 
  },
  { 
    title: '4. Which treatment will you get?',
    protocolSources: PROTOCOL_SOURCE_MAPPINGS['4. Which treatment will you get?'],
    prompt: `You are an expert clinical-regulatory medical writer drafting an Informed Consent Form (ICF) for a global oncology clinical trial. Write only Section 4: Which Treatment Will You Get?


Requirements

Use clear, patient-friendly language suitable for approximately an 8th-grade reading level.

Use a question-and-answer format.

Write directly to the participant using "you" and "your."

Use short sentences, plain language, and short paragraphs.

Avoid unnecessary medical, scientific, legal, or regulatory jargon.

If a medical term must be used, explain it in simple language.

Ensure the content aligns with FDA informed consent requirements, ICH E6(R3), and international ethics committee expectations.

Explain treatment assignment in a way that participants can easily understand.

Focus on treatment allocation, study treatment(s), randomization, placebo use, blinding, and treatment groups.

Do not discuss detailed study procedures, risks, benefits, privacy, costs, compensation, injury care, alternatives, withdrawal, or signatures.

Maintain a neutral, factual, and non-promotional tone.

Use only information provided in the source materials.

Do not invent treatment groups, allocation ratios, placebo use, treatment schedules, or study design elements.


Source Documents
Use only the following source materials:


Protocol Synopsis

Study Design section

Treatment Assignment / Randomization section

Protocol Section 8, only if needed to clarify treatment administration

Relevant protocol amendments provided

These sources should be treated as the primary source of truth for treatment assignment and study treatment information.


Treatment Information Extraction Requirements
Before drafting:
Identify and extract the following information, if applicable:

Study Treatments

Investigational product(s)

Comparator treatment(s)

Standard-of-care treatment(s)

Placebo(s)

Combination therapies

Background therapies


Treatment Groups

Number of treatment groups

Description of each treatment group

Treatment allocation ratio

Cohort-specific treatment assignments

Phase-specific treatment assignments


Randomization

Whether participants are assigned by chance

Allocation ratio (for example, 1:1, 2:1)

Stratification factors, if important for participant understanding


Blinding

Open-label study

Single-blind study

Double-blind study

Sponsor-blinded elements

Independent review processes, if relevant


Placebo Use
If a placebo is used:


Explain what a placebo is in plain language.

Explain why a placebo is being used.

Explain whether participants may receive placebo alone or placebo with active treatment.


Treatment Changes
Identify whether the protocol allows:


Crossover

Treatment switching

Dose modifications

Treatment interruptions

Treatment discontinuation

Include only high-level information relevant to participant understanding.


Mandatory Writing Rules

Clearly identify all study treatment groups.

Explain treatment assignment in plain language.

If randomization is used:

Explain that assignment is by chance.

Use simple examples such as:


"A computer will assign you to a treatment group by chance, similar to flipping a coin."




If allocation ratios are provided:

Explain the participant's chance of receiving each treatment.


If blinding is used:

Explain who will and will not know which treatment is assigned.

Explain why blinding is used.


If placebo is used:

Clearly define placebo in simple language.

Explain how placebo is used in this study.


Do not overemphasize investigational treatment.

Present all treatment groups in a balanced manner.

Do not imply that one treatment is better than another.

Do not include detailed risk information.

Do not repeat detailed procedural information already covered in Section 3.


Study Information
[Insert protocol-specific information]


Include the Following Questions and Answers
What treatments are being studied?
Describe:


The study treatment(s).

Investigational treatment(s).

Comparator treatment(s).

Placebo(s), if applicable.

Combination treatments, if applicable.

Explain treatment names and purposes using patient-friendly language.
At the end of the answer, include a brief statement that information about study procedures is described elsewhere in the consent form.


How will my treatment be chosen?
Describe:


Whether treatment assignment is randomized.

How assignment occurs.

Treatment allocation ratio, if applicable.

Requirements:


Explain randomization in simple language.

Explain participants' chances of receiving each treatment group when applicable.


What are the treatment groups in this study?
Describe:


Each treatment group.

Which treatments are received in each group.

Cohort-specific treatment groups, if applicable.

Requirements:


Present all treatment groups clearly and neutrally.

Avoid excessive protocol terminology.


What is a placebo and will I receive one?
Include this question only if a placebo is used in the study.
Describe:


What a placebo is.

Why placebo is being used.

Whether placebo is given alone or together with active treatment.

The participant's chance of receiving placebo, if applicable.

Use simple, patient-friendly language.


Will I know which treatment I receive?
Describe:


Whether the study is open-label, single-blind, or double-blind.

Who will know the treatment assignment.

Under what circumstances treatment assignment may be disclosed.

Explain the purpose of blinding in simple language.
If the study is open-label, clearly state that participants and study staff will know which treatment is assigned.


Can my study treatment change during the study?
Describe, if applicable:


Treatment switching.

Crossover options.

Dose changes.

Temporary treatment interruptions.

Permanent treatment discontinuation.

Provide only high-level information relevant to participant understanding.
At the end of the answer, include a brief statement that circumstances under which participation may end are described later in the consent form.


Style Instructions

Use the question headings exactly as written above.

Include only questions that apply to the study design.

For example, omit the placebo question if no placebo is used.

Omit treatment-change questions if not applicable.


Keep explanations concise, clear, and easy to understand.

Use short paragraphs and bullet points where appropriate.

Avoid repeating information from previous sections.

Avoid discussing detailed risks or benefits.

Avoid discussing privacy, costs, compensation, injury care, alternatives, withdrawal rights, or signatures.

Integrate any cross-reference naturally into the text rather than using standalone section references.

Ensure participants can clearly understand:

What treatments are being studied,

How treatment assignment works,

Whether treatment assignment is random,

Whether placebo is used,

Whether treatment assignment is blinded, and

Whether treatment may change during the study.`, 
    context: '' 
  },
  { 
    title: '5. Risks and discomforts',
    protocolSources: PROTOCOL_SOURCE_MAPPINGS['5. Risks and discomforts'],
    prompt: `You are an expert clinical-regulatory medical writer drafting an Informed Consent Form (ICF) for a global oncology clinical trial. Write only Section 5: Risks and Discomforts.


Requirements

Use clear, patient-friendly language suitable for approximately an 8th-grade reading level.

Use a question-and-answer format.

Write directly to the participant using "you" and "your."

Use short sentences, plain language, and short paragraphs.

Avoid unnecessary medical, scientific, legal, or regulatory jargon.

If a medical term must be used, explain it in simple language.

Ensure the content aligns with FDA informed consent requirements, ICH E6(R3), and international ethics committee expectations.

Present risks in a balanced, factual, and non-alarming manner.

Clearly explain both known and potential unknown risks.

Include risks related to study treatment(s), study procedures, sample collection, biopsies, imaging, questionnaires (if relevant), pregnancy, and any optional activities.

Distinguish between risks that apply to all participants and risks that apply only to participants undergoing specific procedures or optional activities.

Do not discuss study procedures in detail, treatment assignment methodology, privacy, costs, compensation, alternatives, withdrawal rights, or signatures.

Do not minimize or exaggerate risks.

Use only information provided in the source materials.

Do not invent risks or safety information.


Source Documents
Use only the following source materials:


Protocol Synopsis

Investigator's Brochure safety information (if provided)

Protocol Risk/Benefit sections

Protocol Section 8 (for procedure-related risks only when needed)

Protocol safety sections

Protocol pregnancy/contraception requirements

Optional procedure descriptions

Relevant protocol amendments provided

These documents should be treated as the primary source of truth for risk information.


Risk Extraction Requirements
Before drafting:
Identify and extract all participant-relevant risks, including risks associated with:

Study Treatment Risks

Investigational products

Comparator products

Standard-of-care therapies used in the study

Combination therapies

Placebos (if applicable)


Procedure-Related Risks

Blood sample collection

Urine collection (if relevant)

Tissue collection

Tumor biopsies

Bone marrow procedures

Lumbar punctures

Imaging procedures

Radiation exposure

Device-related procedures

Other protocol-required procedures


Sample Collection and Research Testing Risks

Biomarker testing

Genetic/genomic testing

Optional research samples

Future research samples (if applicable)


Reproductive Risks

Pregnancy-related risks

Fetal risks

Risks related to breastfeeding

Risks related to reproductive potential


Optional Activity Risks
If the study includes optional procedures:


Optional biopsies

Optional genetic testing

Optional sample collection

Optional imaging

Optional research activities

Identify and describe risks separately from mandatory study activities.


Unknown Risks
Identify and explain:


Risks that are not yet known.

Unexpected side effects.

Risks associated with investigational treatments.


Risk Categorization Requirements
When supported by the source materials:
Group risks into patient-friendly categories such as:

Risks Related to Study Treatment
Risks Related to Study Procedures
Risks Related to Sample Collection or Testing
Risks Related to Pregnancy or Reproduction
Risks Related to Optional Activities
Unknown or Unexpected Risks
Do not create categories that are not supported by the source materials.


Mandatory Writing Rules

Clearly explain that all medical treatments and research procedures may involve risks.

Explain that some side effects may be serious.

Explain that some risks may be unknown.

Explain that participants should report side effects or health changes to the study team.

Clearly identify optional-procedure risks as applying only to participants who choose those activities.

Explain reproductive risks whenever applicable.

Avoid extensive lists of highly technical adverse events.

Translate medical terminology into plain language whenever possible.

If frequency information is provided in the source materials, explain it using patient-friendly language such as:

Very common

Common

Uncommon

Rare


If frequency information is not available, do not create frequency estimates.

Present risks objectively without creating unnecessary fear.

Do not repeat procedural details already described in Section 3.


Study Information
[Insert protocol-specific information]


Include the Following Questions and Answers
What are the possible risks of taking part in this study?
Provide:


A general introduction explaining that all research studies involve risks.

A statement that some side effects or complications may be serious.

A statement that some risks may not yet be known.

This question should serve as the overview of the risk section.


What risks are related to the study treatment?
Describe:


Known risks of investigational treatment(s).

Known risks of comparator treatment(s), if applicable.

Known risks of combination treatment(s), if applicable.

Requirements:


Use plain language.

Group similar risks together where appropriate.

Explain serious risks separately when helpful.


What risks are related to study procedures?
Describe risks associated with procedures such as:


Blood draws

Tissue collection

Biopsies

Bone marrow sampling

Imaging procedures

Radiation exposure

Device procedures

Other protocol-required procedures

Include only procedures that apply to the study.


Are there risks related to samples, biomarker testing, or genetic testing?
Include this question only if applicable.
Describe:


Risks associated with sample collection.

Risks associated with genetic or genomic testing, if described in the source materials.

Any participant-facing implications identified in the protocol.

Do not discuss privacy-related issues unless explicitly required by the protocol and not covered elsewhere.


Are there risks related to pregnancy, breastfeeding, or reproduction?
Include this question whenever applicable.
Describe:


Known or potential risks to an unborn baby.

Risks related to becoming pregnant during the study.

Risks related to fathering a child during the study, if applicable.

Breastfeeding restrictions, if applicable.

Use clear, patient-friendly language.


Are there risks related to optional activities?
Include this question only if optional procedures exist.
Describe:


Optional biopsies.

Optional genetic testing.

Optional sample collection.

Optional imaging.

Optional research activities.

Clearly state that these risks apply only if the participant chooses to take part in the optional activity.


Are there risks that are not yet known?
Describe:


Unknown risks.

Unexpected side effects.

Risks associated with investigational treatments.

The possibility that new information may become available during the study.

At the end of the answer, include a brief statement that participants will be informed of important new information that may affect their willingness to continue in the study.


What should I do if I experience side effects or health problems during the study?
Describe:


The importance of reporting side effects promptly.

Contacting the study doctor or study team.

Seeking urgent medical attention when appropriate.

Do not include contact details in this section.


Style Instructions

Use the question headings exactly as written above.

Include only questions that apply to the study.

Keep explanations concise, clear, and easy to understand.

Use short paragraphs and bullet points where appropriate.

Group similar risks together to improve readability.

Avoid overwhelming participants with large adverse-event tables.

Avoid repeating procedural details already described elsewhere.

Avoid discussing privacy, compensation, costs, alternatives, withdrawal rights, or signatures.

Integrate any cross-reference naturally into the text rather than using standalone section references.

Ensure participants can clearly understand:

The main risks of participation,

Risks related to study treatment,

Risks related to procedures,

Risks related to pregnancy and reproduction,

Risks related to optional activities, and

The possibility of unknown risks.`, 
    context: '' 
  },
  { 
    title: '6. Possible benefits',
    protocolSources: PROTOCOL_SOURCE_MAPPINGS['6. Possible benefits'],
    prompt: `You are an expert clinical-regulatory medical writer drafting an Informed Consent Form (ICF) for a global oncology clinical trial. Write only Section 6: Possible Benefits.


Requirements

Use clear, patient-friendly language suitable for approximately an 8th-grade reading level.

Use a question-and-answer format.

Write directly to the participant using "you" and "your."

Use short sentences, plain language, and short paragraphs.

Avoid unnecessary medical, scientific, legal, or regulatory jargon.

If a medical term must be used, explain it in simple language.

Ensure the content aligns with FDA informed consent requirements, ICH E6(R3), and international ethics committee expectations.

Present benefits in a balanced, factual, neutral, and non-promotional manner.

Clearly distinguish between:

Possible benefits to the participant, and

Possible benefits to future patients, medical knowledge, or society.


Clearly state when direct benefit is uncertain or cannot be guaranteed.

Avoid language that may create unrealistic expectations or therapeutic misconception.

Do not imply that participation is better than standard care.

Do not imply that study treatment is effective simply because it is being studied.

Avoid language that is promotional, overly optimistic, overly negative, alarming, or discouraging.

Present uncertainty in a neutral manner consistent with the investigational nature of the study.

Use only information provided in the source materials.

Do not invent benefits, efficacy claims, or outcomes.

Do not discuss detailed risks, procedures, privacy, costs, compensation, alternatives, withdrawal rights, or signatures.

Present benefits in a balanced manner that is consistent with the risks described elsewhere in the consent form.


Source Documents
Use only the following source materials:


Protocol Synopsis

Study Objectives

Protocol Risk-Benefit Assessment

Investigator's Brochure benefit-related information (if provided)

Clinical rationale sections

Relevant protocol amendments provided

These documents should be treated as the primary source of truth for benefit information.


Benefit Extraction Requirements
Before drafting:
Identify and extract information regarding:

Potential Direct Benefits
Examples may include:


Tumor response
Disease control
Delayed disease progression
Symptom improvement
Improved quality of life
Access to investigational treatment
Include only benefits that are explicitly supported by the study documents.


Potential Indirect Benefits
Examples may include:

Additional health monitoring
Additional assessments
More frequent health evaluations

Only include these if supported by the protocol and appropriate for participant understanding.
Do not imply that additional monitoring itself is a clinical benefit.


Benefits to Future Patients and Medical Knowledge
Examples may include:


Better understanding of the disease

Development of future treatments

Improved treatment strategies

Advancement of medical knowledge


Situations Where Direct Benefit Is Uncertain or Not Expected
If the protocol states that direct benefit is uncertain, unlikely, or not expected:
Communicate this clearly using patient-friendly language.
Examples:


"You may or may not benefit from taking part in this study."
"The study treatment may  or may not help your condition." "Researchers do not know whether the study treatment will benefit participants, but you will be under the medical doctors complete supersision.."



Mandatory Writing Rules

Clearly explain that benefits cannot be guaranteed.

Clearly explain that participants may or may not experience a direct benefit from participation.

Distinguish between:

Possible benefits to the participant, and

Possible benefits to future patients and medical research.


If direct benefit is uncertain, clearly state this.

If no direct benefit is expected, clearly state this.

Use neutral wording such as:

"may"

"might"

"it is not known whether"

"cannot be guaranteed"


Avoid emotionally charged or persuasive language.

Avoid promotional language such as:

"exciting treatment"

"promising therapy"

"breakthrough treatment"

"superior treatment"


Avoid framing uncertainty as treatment failure.

Avoid statements suggesting that participation is likely to improve outcomes.

Do not repeat detailed efficacy data from the protocol.

Do not present preliminary findings as established benefits.

Ensure the overall tone remains informative, objective, and balanced.


Study Information
[Insert protocol-specific information]


Include the Following Questions and Answers
Will I benefit from taking part in this study?
Describe:


Potential direct benefits, if any.

Whether direct benefit is uncertain.

Whether participants may not experience any direct benefit.

Requirements:


Clearly explain that benefits cannot be guaranteed.

Use balanced and neutral language.

If no direct benefit is expected, state this clearly.


What benefits might result from this study?
Describe:


Potential benefits to future patients.

Potential benefits to medical knowledge.

How the information collected may help researchers better understand the disease or its treatment.

Requirements:


Focus on possible contributions to research and future patient care.

Avoid overstating the impact of the study.


What if I do not experience a benefit from the study?
Describe:


The study treatment may or may not help the participant.

The disease may improve, remain unchanged, or worsen.

Researchers are conducting the study because the effects of the study treatment are not fully known.

Requirements:


Use neutral language.

Avoid wording that implies treatment failure.

Reinforce realistic expectations regarding participation.


How may information from this study contribute to future research?
Describe:


How study findings may contribute to medical knowledge.

How the information collected may help researchers learn more about the disease and its treatment.

How future research or treatment development may benefit from the study results.

Do not discuss privacy protections or data handling details in this section.


Style Instructions

Use the question headings exactly as written above.

Keep explanations concise, clear, and easy to understand.

Use short paragraphs and bullet points where appropriate.

Present benefits and uncertainties in a neutral, balanced manner that is consistent with the overall tone of the consent form.

Avoid language that is promotional, overly optimistic, overly negative, alarming, or discouraging.

Avoid repeating information from previous sections.

Avoid discussing privacy, compensation, costs, alternatives, withdrawal rights, or signatures.

Avoid discussing detailed study results or efficacy statistics.

Integrate any cross-reference naturally into the text rather than using standalone section references.

Ensure participants can clearly understand:

Whether they may benefit personally,

That benefits cannot be guaranteed,

That they may or may not experience a benefit,

How future patients may benefit, and

How the study may contribute to medical knowledge and future research.`,
    context: '' 
  },
  { 
    title: '7. Your choices (alternatives)',
    protocolSources: PROTOCOL_SOURCE_MAPPINGS['7. Your choices (alternatives)'],
    prompt: `You are an expert clinical-regulatory medical writer drafting an Informed Consent Form (ICF) for a global oncology clinical trial. Write only Section 7: Your Choices (Alternatives).


Requirements
Use clear, patient-friendly language suitable for approximately an 8th-grade reading level.
Use a question-and-answer format.
Write directly to the participant using "you" and "your."
Use short sentences, plain language, and short paragraphs.
Avoid unnecessary medical, scientific, legal, or regulatory jargon.
If a medical term must be used, explain it in simple language.
Ensure the content aligns with FDA informed consent requirements, ICH E6(R3), and international ethics committee expectations.
Clearly explain that participation is voluntary.
Clearly explain that participants have alternatives to joining the study.
Present alternatives in a neutral, balanced, and non-directive manner.
Use balanced, participant-centered language.
Avoid wording that is overly positive, overly negative, persuasive, defensive, coercive, or discouraging.
Prefer neutral sentence construction where possible.
Present participant choice as an available option rather than framing decisions as refusal.
When answering questions about participation decisions, begin with a statement that participation is voluntary rather than with "Yes" or "No."
Do not encourage or discourage participation.
Use only information provided in the study documents.
Do not invent treatment options, medical recommendations, or standards of care.
Do not discuss detailed risks, benefits, privacy, costs, compensation, injury care, withdrawal after enrollment, or signatures.


Source Documents
Use only the following source materials:

Protocol Synopsis
Protocol Background and Rationale sections
Standard-of-care information provided in study documents
Investigator's Brochure (if relevant)
Relevant protocol amendments provided

These documents should be treated as the primary source of truth for alternatives information.


Alternative Treatment Extraction Requirements
Before drafting:
Identify any alternatives to study participation that are described or implied in the study documents.
These may include:

Approved standard treatments
Other available medical treatments
Supportive care
Palliative care
Observation/watchful waiting
No treatment, when appropriate
Other clinical trials
Best supportive care
Symptom management approaches

Only include alternatives that are supported by the source materials or are appropriate standard alternatives typically expected for informed consent purposes.


Mandatory Writing Rules
Clearly explain that participation is voluntary.
Clearly explain that participants may decide whether participation is right for them.
Clearly explain that medical care, benefits, legal rights, and access to treatment are not affected by the participation decision.
Clearly explain that alternatives to study participation may be available.
Encourage participants to discuss their options with:
The study doctor,
Their treating physician,
Family members,
Caregivers,
Others they trust, if appropriate.

Present alternatives neutrally.
Do not imply that participation is preferable to alternatives.
Do not imply that alternatives are preferable to participation.
Avoid making treatment recommendations.
Avoid detailed discussions of risks and benefits of alternative treatments unless specifically required by local regulations or source materials.
Use neutral language such as:

"Participation is voluntary."
"You may choose whether participation is right for you."
"Several options may be available to you."
"You may wish to discuss these options with your doctor."

Avoid opening responses with:

"Yes."
"No."
"You do not have to..."
"You are not required to..."

unless necessary for accuracy.


Study Information
[Insert protocol-specific information]


Include the Following Questions and Answers
Do I have to take part in this study?
Describe:

Participation is voluntary.
You may decide whether participation is right for you.
Choosing not to participate will not affect your regular medical care, benefits, or legal rights.
You may discuss your decision with your doctors, family members, caregivers, or others you trust.

Requirements:

Begin the answer with a statement that participation is voluntary.
Do not begin the answer with "No" or similar negative wording.
Use balanced, supportive language.
Avoid coercive, persuasive, or defensive wording.


What other choices do I have?
Describe alternatives that may be available, such as:

Standard treatments.
Other approved treatments.
Other research studies or clinical trials.
Supportive care.
Symptom management.
Observation or monitoring.
No treatment, if appropriate.

Requirements:

Include only alternatives supported by the study documents or appropriate standard alternatives for informed consent.
Present alternatives in a neutral and balanced manner.
Explain that available alternatives may differ from person to person.


Should I discuss my options with my doctor?
Describe:

The importance of discussing available options with healthcare providers.
That the study doctor or treating physician can answer questions about available alternatives.
That participants should consider all available options before deciding.

Requirements:

Maintain a neutral tone.
Do not provide treatment recommendations.


Can I decide not to join the study?
Describe:

Participation is voluntary.
Participants may decide that study participation is not the right choice for them.
Medical care will continue regardless of the decision.
Benefits and legal rights are not affected by the participation decision.

Requirements:

Begin the answer with a statement that participation is voluntary.
Do not begin the answer with "Yes" or "No."
Reinforce participant choice.
Use neutral, patient-friendly language.
Avoid wording that suggests refusal, loss, or penalty.


Style Instructions
Use the question headings exactly as written above.
Keep explanations concise, clear, and easy to understand.
Use short paragraphs and bullet points where appropriate.
Present alternatives in a balanced and non-directive manner.
Avoid repeating detailed information from earlier sections.
Avoid discussing detailed risks or benefits of specific treatments.
Avoid discussing privacy, compensation, costs, injury care, withdrawal after enrollment, or signatures.
Integrate any cross-reference naturally into the text rather than using standalone section references.
For questions that could be answered with "Yes" or "No," provide explanatory answers instead of starting with a one-word response.
Prefer:


"Participation is voluntary."


over:


"No, you do not have to participate."


Prefer:


"Participation is voluntary, and you may decide whether joining is right for you."


over:


"Yes, you can decide not to join."


Ensure participants can clearly understand:
Participation is voluntary,
Alternatives to participation may be available,
Medical care is not affected by the participation decision, 
They can discuss options with healthcare providers before deciding.`, 
    context: '' 
  },
  { 
    title: '8. Privacy & use of your information',
    protocolSources: PROTOCOL_SOURCE_MAPPINGS['8. Privacy & use of your information'],
    prompt: `You are an expert clinical-regulatory medical writer drafting an Informed Consent Form (ICF) for a global oncology clinical trial. Write only Section 8: Privacy & Use of Your Information.


Requirements

Use clear, patient-friendly language suitable for approximately an 8th-grade reading level.

Use a question-and-answer format.

Write directly to the participant using "you" and "your."

Use short sentences, plain language, and short paragraphs.

Avoid unnecessary medical, scientific, legal, or regulatory jargon.

If a technical term must be used (for example, coded data, pseudonymized data, data protection authority), explain it in simple language.

Ensure the content aligns with FDA informed consent requirements, ICH E6(R3), GDPR principles (where applicable), HIPAA concepts (where applicable), and international ethics committee expectations.

Present information in a neutral, transparent, and factual manner.

Focus on:

Collection of participant information,

Use of participant information,

Sharing of participant information,

Protection of participant privacy,

Access to study records,

Retention of information,

Use of study results.


Do not discuss study procedures, risks, benefits, alternatives, costs, compensation, injury care, withdrawal from treatment, or signatures except where directly relevant to data handling.

Use only information provided in the source materials.

Do not invent privacy protections, legal rights, retention periods, recipients, or data uses.


Source Documents
Use only the following source materials:


Protocol confidentiality and data handling sections

Protocol data protection sections
Sponsor privacy language provided for the study
Sample and biomarker handling sections (only as relevant to information use)
Relevant protocol amendments provided
These documents should be treated as the primary source of truth for privacy and information-use content.


Information Extraction Requirements
Before drafting:
Identify and extract information regarding:

Information Collected
Examples may include:


Medical information

Study records

Laboratory results

Imaging results

Health information

Demographic information

Study questionnaire responses

Electronic diary information

Device-generated information

Genetic or biomarker information (if applicable)


How Information Will Be Used
Examples may include:


Conducting the study

Evaluating study treatment safety

Evaluating study treatment effectiveness

Regulatory submissions

Medical research

Publication of study findings

Future scientific analyses allowed by the protocol


Who May Access Information
Examples may include:


Study doctor and study staff

Sponsor

Sponsor representatives

Contract research organizations

Regulatory authorities

Ethics committees or institutional review boards

Auditors and inspectors

Laboratories supporting the study


Information Sharing
Examples may include:


Sharing with research partners

Sharing with regulators

International data transfers

Sharing with service providers supporting the study


Confidentiality Protections
Examples may include:


Use of participant study numbers

Coding of information

Limited direct identifiers

Secure storage methods

Restricted access


Data Retention
Identify:


Retention periods

Conditions for retention

Regulatory requirements for retention

Include only if specified in the source materials.


Genetic Information
If applicable, identify:


Collection of genetic information

Use of genetic information

Storage of genetic information

Sharing of genetic information

Only include participant-facing information supported by the source materials.


Mandatory Writing Rules

Clearly explain that information collected during the study will be used for research purposes.

Clearly explain that reasonable efforts will be made to protect participant privacy.

Clearly explain that complete confidentiality cannot be guaranteed.

Explain that information may be reviewed by authorized individuals or organizations involved in study oversight.

Explain that study results may be published or presented, but participants will not be personally identified.

Explain data coding or participant identification methods in plain language, if applicable.

If international transfer of information is described, explain this in simple language.

If genetic information is collected, explain how it may be used and protected.

Avoid overly legalistic language.

Avoid detailed legal citations or regulatory references.

Avoid making guarantees that are not supported by the source materials.

Present privacy protections accurately and transparently.


Study Information
[Insert protocol-specific information]


Include the Following Questions and Answers
What information about me will be collected during this study?
Describe:


Types of information collected.

Information from study visits and assessments.

Health information collected during participation.

Questionnaire, electronic diary, device, imaging, laboratory, biomarker, or genetic information, if applicable.

Requirements:


Use plain language.

Focus on participant understanding.


How will my information be used?
Describe:


Why information is collected.

How it will be used for research purposes.

How it may be used to evaluate study treatment safety and effectiveness.

How it may be used for regulatory and scientific purposes.

Requirements:


Use clear, factual language.

Avoid unnecessary technical detail.


Who may see or review my information?
Describe:


Individuals and organizations that may access study information.

Oversight and regulatory review.

Sponsor review.

Research support organizations, if applicable.

Requirements:


Explain access in a participant-friendly manner.

Emphasize that access is limited to authorized persons.


How will my privacy be protected?
Describe:


Coding or de-identification methods, if applicable.

Measures used to protect information.

Limits of confidentiality.

Storage and security protections, if described.

Requirements:


Explain privacy protections clearly.

Avoid guarantees of absolute confidentiality.


Will my information be shared outside my country?
Include only if applicable.
Describe:


International transfer of study information.

Why information may be transferred.

How information will continue to be protected.

Requirements:


Use simple language.

Explain international transfers in a balanced manner.


What happens to my information after the study ends?
Describe:


Retention of study information.

Future permitted use of study information.

Regulatory requirements for record retention, if applicable.

Continued protection of participant privacy.

Requirements:


Include only information supported by the source materials.


Will study results be shared?
Describe:


Publication or presentation of study findings.

That participants will not be personally identified in publications or reports.

Any participant access to study results, if described elsewhere in the study materials.

Requirements:


Use plain language.

Avoid promising individual study results unless supported by the source materials.


Style Instructions

Use the question headings exactly as written above.

Include only questions that apply to the study.

Keep explanations concise, clear, and easy to understand.

Use short paragraphs and bullet points where appropriate.

Present privacy information in a transparent and balanced manner.

Avoid repeating information from earlier sections.

Avoid discussing risks, benefits, alternatives, compensation, costs, injury care, or signatures.

Integrate any cross-reference naturally into the text rather than using standalone section references.

Ensure participants can clearly understand:

What information is collected,

How information is used,

Who may access information,

How privacy is protected,

Whether information may be transferred internationally,

What happens to information after the study ends.`, 
    context: '' 
  },
  { 
    title: '9. Costs, payments, injury care',
    protocolSources: PROTOCOL_SOURCE_MAPPINGS['9. Costs, payments, injury care'],
    prompt: `You are an expert clinical-regulatory medical writer drafting an Informed Consent Form (ICF) for a global oncology clinical trial. Write only Section 9: Costs, Payments, and Injury Care.


Requirements

Use clear, patient-friendly language suitable for approximately an 8th-grade reading level.

Use a question-and-answer format.

Write directly to the participant using "you" and "your."

Use short sentences, plain language, and short paragraphs.

Avoid unnecessary medical, scientific, legal, insurance, or regulatory jargon.

If a technical term must be used, explain it in simple language.

Ensure the content aligns with FDA informed consent requirements, ICH E6(R3), and international ethics committee expectations.

Present information in a neutral, transparent, factual, and participant-centered manner.

Use balanced, participant-centered language.

Avoid wording that may appear promotional, alarming, defensive, legalistic, coercive, or discouraging.

Prefer neutral sentence construction where possible.

Focus on helping participants understand what costs, reimbursements, payments, and injury-related provisions may apply to them.

When discussing injury care, focus first on available support, medical care, and next steps before discussing limitations or conditions.

Avoid language that could be interpreted as minimizing participant rights or concerns.

Clearly distinguish among:

Study-related costs,

Reimbursements and payments,

Compensation (if applicable),

Insurance/billing responsibilities,

Injury care and compensation for research-related injury.


Use only information provided in the source materials.

Do not invent payment amounts, reimbursement policies, insurance coverage, injury compensation provisions, sponsor commitments, participant costs, or billing arrangements.


Source Documents
Use only the following source materials:


Clinical Trial Agreement participant-facing provisions (if provided)

Sponsor compensation/reimbursement language

Protocol financial and reimbursement sections

Injury compensation language

Insurance language

Country-specific participant compensation language

Ethics committee-approved compensation language

Relevant protocol amendments provided

These documents should be treated as the sole source of truth for cost, payment, reimbursement, insurance, and injury-care information.


Information Extraction Requirements
Before drafting, extract and document only information explicitly stated in the source materials.

Study Costs
Identify:


Costs covered by the study.

Costs covered by the sponsor.

Costs covered by healthcare systems or insurers, if stated.

Costs that are the participant's responsibility, if stated.

Statements indicating there are no participant costs.

Statements regarding routine medical care costs, if stated.

Statements regarding insurance billing, if stated.

Do not assume that participant costs, insurance billing, routine medical care costs, co-payments, deductibles, or financial responsibilities exist unless explicitly stated.
If the source materials do not address participant costs, do not speculate.


Reimbursement and Payments
Identify:


Travel reimbursement.

Meal reimbursement.

Parking reimbursement.

Lodging reimbursement.

Stipends.

Compensation for time and inconvenience.

Payment schedules.

Conditions for reimbursement or payment.

Include only what is explicitly described in the source materials.


Insurance and Billing
Identify:


Insurance billing practices.

Sponsor payment obligations.

Participant payment obligations.

Standard-of-care billing arrangements.

Include only what is explicitly described in the source materials.


Research-Related Injury Care
Identify:


Medical care available for research-related injury.

Who provides or arranges injury care.

Who may pay for injury treatment.

Insurance provisions.

Sponsor obligations.

Country-specific requirements.

Include only what is explicitly described in the source materials.


Compensation for Injury
Identify whether:


Compensation is available.

Compensation is not available.

Compensation depends on local laws.

Compensation depends on sponsor policy.

Compensation is subject to specific conditions.

Include only what is explicitly stated in the source materials.


Mandatory Writing Rules

Determine from the source materials whether participation results in any participant financial responsibility.

Describe only those costs that are explicitly identified in the source materials.

If no participant costs are described, state this clearly and do not speculate about possible costs.

Do not assume that routine medical care, insurance billing, co-payments, deductibles, participant expenses, or standard healthcare charges apply unless explicitly stated.

Clearly explain whether reimbursement or payment is available.

Clearly explain the difference between:

Reimbursement of expenses, and

Payment for participation, if both apply.


Clearly explain what participants should do if they believe they have experienced a research-related injury.

Clearly explain available medical care for research-related injuries.

Clearly explain compensation provisions only as supported by the source materials.

Avoid legalistic language whenever possible.

Do not imply broader compensation rights than those described in the source materials.

Do not guarantee free medical care unless specifically stated.

Present financial information objectively and transparently.

Present cost and reimbursement information factually without emphasizing financial incentives.

When describing reimbursements or payments:


Explain them as support for participation-related expenses or time, where applicable.

Avoid language that could be interpreted as encouraging participation.


When describing research-related injury:


Begin by explaining available medical care or support.

Then explain any applicable payment, insurance, or compensation provisions.


Avoid opening answers with:


"Yes."

"No."

"You must pay..."

"You will not receive..."

"No compensation will be provided..."

unless required for accuracy by the source materials.


Where compensation is not available, present the information factually and neutrally without emphasizing the absence of compensation.

The source documents are the sole authority for financial responsibility.

Do not infer, assume, or introduce:


Participant payments,

Insurance billing,

Routine care costs,

Co-payments,

Deductibles,

Reimbursement limitations,

Sponsor coverage,

Injury compensation rights,

unless explicitly described in the source materials.


Study Information
[Insert protocol-specific information]


Include the Following Questions and Answers
Will I have to pay for anything if I take part in this study?
Describe:


What costs, if any, are covered by the study.

What costs, if any, are the participant's responsibility.

Whether insurance billing applies, if stated in the source materials.

Whether participation involves no additional costs, if stated in the source materials.

Requirements:


Use only information explicitly stated in the source materials.

Do not assume participant costs exist.

Do not assume insurance billing applies.

Do not describe standard medical care costs unless specifically addressed in the source materials.

If participation does not result in participant costs, state this clearly.

If costs are not addressed in the source materials, do not speculate.

Begin with a neutral explanation of study-related cost arrangements.

Do not begin with "Yes" or "No."


Will I receive any payment or reimbursement?
Describe:


Reimbursement of study-related expenses.

Travel, parking, meals, lodging, or other reimbursements, if applicable.

Payments, stipends, or compensation for time and inconvenience, if applicable.

Conditions that apply to reimbursement or payment, if described.

Requirements:


Clearly distinguish reimbursement from payment.

Include only information supported by the source materials.

Begin with a neutral explanation of available reimbursement or payment arrangements.

Do not begin with "Yes" or "No."


What happens if I am injured because of this study?
Describe:


Research-related injury.

Available medical treatment or support.

Whom participants should contact if they believe they have been injured because of the study.

How injury-related concerns will be addressed.

Requirements:


Use clear and supportive language without making unsupported promises.

Focus on participant understanding.

Begin by explaining available support and medical care.


Who will pay for treatment of a research-related injury?
Describe:


Sponsor responsibilities, if applicable.

Insurance responsibilities, if applicable.

Participant responsibilities, if applicable.

Country-specific requirements, if applicable.

Requirements:


Include only information supported by the source materials.

Avoid legal interpretation.

Present information factually and neutrally.

Do not begin with "Yes" or "No."


Will I receive compensation for a research-related injury?
Include only if applicable or required by local requirements.
Describe:


Whether compensation may be available.

Any limitations or conditions described in the source materials.

Any legally required compensation provisions.

Requirements:


Use neutral and factual language.

Avoid creating expectations beyond what is stated in the source materials.

Do not begin with "Yes" or "No."


Style Instructions

Use the question headings exactly as written above.

Include only questions that apply to the study and local requirements.

Keep explanations concise, clear, and easy to understand.

Use short paragraphs and bullet points where appropriate.

Present financial information in a neutral and informative manner.

Present injury-care information in a supportive and participant-focused manner.

When a question could be answered with a simple "Yes" or "No," provide an explanatory answer instead of starting with a one-word response.

Emphasize participant understanding rather than legal or administrative requirements.

Use language that is clear, respectful, and balanced throughout the section.

Avoid repeating information from earlier sections.

Avoid discussing study risks, benefits, alternatives, privacy, withdrawal rights, or signatures.

Integrate any cross-reference naturally into the text rather than using standalone section references.

Ensure participants can clearly understand:

What costs may apply (if any),

What costs are covered by the study,

Whether reimbursement or payment is available,

What to do if they experience a research-related injury,

Whether compensation for injury may be available.`, 
    context: '' 
  },
  { 
    title: '10. Pregnancy & contraception (if applicable)',
    protocolSources: PROTOCOL_SOURCE_MAPPINGS['10. Pregnancy & contraception (if applicable)'],
    prompt: `You are an expert clinical-regulatory medical writer drafting an Informed Consent Form (ICF) for a global oncology clinical trial. Write only Section 10: Pregnancy and Contraception.


Requirements

Use clear, patient-friendly language suitable for approximately an 8th-grade reading level.

Use a question-and-answer format.

Write directly to the participant using "you" and "your."

Use short sentences, plain language, and short paragraphs.

Avoid unnecessary medical, scientific, legal, or regulatory jargon.

If a medical term must be used, explain it in simple language.

Ensure the content aligns with FDA informed consent requirements, ICH E6(R3), CIOMS guidance, and international ethics committee expectations.

Present information in a neutral, factual, respectful, and participant-centered manner.

Avoid wording that is alarming, judgmental, coercive, or stigmatizing.

Include only pregnancy, reproductive risk, contraception, pregnancy testing, breastfeeding, fertility, partner-related requirements, and pregnancy reporting requirements that are explicitly described in the source materials.

Do not invent reproductive risks, contraception requirements, fertility effects, pregnancy testing requirements, partner requirements, or breastfeeding restrictions.

Do not discuss general study risks except where directly related to pregnancy or reproduction.


Applicability Determination
Before drafting, determine whether this section applies to the study.

Include this section only if the source materials indicate one or more of the following:

Pregnancy-related risks.

Fetal risk.

Embryo-fetal toxicity concerns.

Contraception requirements.

Pregnancy testing requirements.

Restrictions regarding sperm donation.

Restrictions regarding egg donation.

Restrictions regarding breastfeeding.

Reproductive precautions for male participants.

Reproductive precautions for female participants.

Pregnancy reporting requirements.

If none of the above apply, generate the following statement only:


Pregnancy, contraception, breastfeeding, and reproductive precautions do not apply to participation in this study.


Do not create additional content.


Source Documents
Use only the following source materials:


Protocol contraception and reproductive risk sections.

Inclusion and exclusion criteria.

Investigator's Brochure reproductive safety information.

Risk management plans.

Protocol safety sections.

Country-specific reproductive risk language, if provided.

Relevant protocol amendments provided.

These documents should be treated as the sole source of truth for reproductive and pregnancy-related requirements.


Information Extraction Requirements
Before drafting, identify and extract:

Pregnancy and Fetal Risks

Known fetal risks.

Potential fetal risks.

Unknown fetal risks.

Embryo-fetal toxicity concerns.

Risks to an unborn child.

Reasons pregnancy precautions are required.


Contraception Requirements
Identify separately for:

Female Participants (if applicable)

Whether contraception is required.

Pregnancy testing requirements.

Required duration of contraception use.

Acceptable contraception methods.

Egg donation restrictions.

Male Participants (if applicable)

Whether contraception is required.

Condom requirements.

Partner contraception requirements.

Sperm donation restrictions.

Include only what is explicitly stated in the source materials.


Pregnancy Reporting Requirements
Identify:


What participants should do if pregnancy occurs.

Who should be contacted.

Whether study treatment must be stopped.

Any follow-up requirements.

Information collection requirements.

Include only what is explicitly described in the source materials.


Additional Reproductive Precautions
Identify and include only if described:


Breastfeeding restrictions.

Fertility information.

Partner-related precautions.

These should be incorporated into the relevant questions below rather than creating additional questions.


Mandatory Writing Rules

Include only information explicitly stated in the source materials.

Clearly explain any known, possible, or unknown risks to an unborn child, if applicable.

Clearly explain why pregnancy precautions are required.

Clearly explain any required contraception measures.

Clearly explain pregnancy testing requirements, if applicable.

Clearly explain breastfeeding restrictions within the relevant answer, if applicable.

Clearly explain sperm donation or egg donation restrictions within the relevant answer, if applicable.

Clearly explain what participants should do if pregnancy occurs during the study.

Present reproductive precautions as safety requirements rather than personal judgments.

Avoid language that implies blame, fault, responsibility, or wrongdoing if pregnancy occurs.

Avoid legalistic, alarmist, or overly technical language.

Do not assume that contraception requirements apply to all participants.

Tailor information to:


Women who can become pregnant,

Women who cannot become pregnant,

Male participants,

Partners,

only when explicitly required by the source materials.


Do not create contraception methods, timelines, restrictions, or monitoring requirements that are not stated in the source materials.

Where reproductive risks are unknown, clearly state this using balanced language.


Study Information
[Insert protocol-specific information]


Include the Following Questions and Answers
Why are pregnancy precautions required?
Describe:


Whether the study treatment may affect an unborn baby.

Whether risks are known, possible, or unknown.

Why pregnancy precautions are required.

Any relevant reproductive safety concerns described in the protocol.

Requirements:


Use balanced and factual language.

Avoid unnecessarily alarming wording.

Explain uncertainty clearly when risks are not fully known.

Include breastfeeding or fertility considerations here if they are relevant and described in the source materials.


What contraception requirements apply to me?
Describe, as applicable:


Who must follow contraception requirements.

Pregnancy testing requirements.

Required duration of contraception use.

Acceptable contraception methods.

Requirements for male participants.

Requirements for female participants.

Partner-related requirements.

Sperm donation restrictions.

Egg donation restrictions.

Requirements:


Include only protocol-required information.

Clearly distinguish requirements that apply to different participant groups.

Use respectful, patient-friendly language.

Do not include requirements that are not explicitly stated in the source materials.


What should I do if pregnancy occurs during the study?
Describe:


Who should be contacted.

What participants should do if pregnancy occurs.

Whether study treatment may need to be stopped, if applicable.

Any follow-up requirements.

Any pregnancy reporting requirements described in the protocol.

Requirements:


Use supportive, non-judgmental language.

Focus on participant safety and study requirements.

Include only actions explicitly described in the source materials.


Style Instructions

Use the question headings exactly as written above.

Do not create additional questions.

Incorporate breastfeeding restrictions, fertility information, sperm donation restrictions, egg donation restrictions, and partner-related precautions into the existing questions where relevant.

Keep explanations concise, clear, and easy to understand.

Use short paragraphs and bullet points where appropriate.

Present reproductive safety information in a balanced and respectful manner.

Avoid repeating detailed study risk information already discussed elsewhere unless specifically relevant to pregnancy or reproduction.

Avoid discussing privacy, costs, compensation, alternatives, withdrawal rights, or signatures.

Integrate any cross-reference naturally into the text rather than using standalone section references.

Ensure participants can clearly understand:

Why pregnancy precautions are required,

Whether reproductive risks are known or unknown,

What contraception requirements apply,

What actions are required if pregnancy occurs during the study.`, 
    context: '' 
  },
  { 
    title: '11. Samples & future research (if applicable)',
    protocolSources: PROTOCOL_SOURCE_MAPPINGS['11. Samples & future research (if applicable)'],
    prompt: `You are an expert clinical-regulatory medical writer
drafting an Informed Consent Form (ICF) for a global oncology clinical trial.
Write only Section 11: Samples and Future Research.
Requirements
Use clear, patient-friendly language suitable for
approximately an 8th-grade reading level.
Use a question-and-answer format.
Write directly to the participant using "you" and "your."
Use short sentences, plain language, and short paragraphs.
Avoid unnecessary medical, scientific, legal, or regulatory jargon.
If a medical term must be used, explain it in simple language.
Ensure the content aligns with FDA informed consent
requirements, ICH E6(R3), CIOMS guidance, and international ethics committee expectations.
Present information in a neutral, factual, transparent, and participant-centered manner.
Avoid wording that is promotional, alarming, coercive, or discouraging.
Include only information explicitly described in the source materials.
Do not invent sample collection, storage, testing, future research uses, genetic analyses, commercial use, or optional research activities.
This section should address only the handling and future use of biological samples and any optional future research. Details of sample collection procedures should already have been explained in Section 3 (What
Will Happen If You Join This Study?) and should not be repeated here.


Applicability Determination
Before drafting, determine whether this section applies.
Include this section only if the source materials describe one or more of the following: 

Collection of biological samples.


Storage of biological samples.


Future research use of samples.


Optional research.


Genetic or genomic testing.


Biomarker testing.


Pharmacogenetic/pharmacogenomic analyses.


Future use of study data linked to samples.


Commercial use of samples.


Sample destruction or disposal.


Withdrawal options for stored samples.


 


If none of these apply, generate the following statement
only:


 


This study does not include storage or future research use
of your samples beyond what is needed for this study.


 


Do not create additional content.


 


Source Documents


 


Use only the following source materials:


 


Protocol laboratory/sample handling sections.


Biomarker and pharmacogenomic sections.


Protocol future research provisions.


Optional research consent language.


Investigator's Brochure (if applicable).


Sample management plans.


Country-specific consent language.


Relevant protocol amendments.


 


These documents are the sole source of truth for this
section.


 


Information Extraction Requirements


 


Before drafting, identify and extract only information
explicitly stated regarding:


 


Samples


Types of ALL samples collected (blood, tissue, urine,
saliva, etc.).


Whether leftover samples will be stored.


Purpose of sample storage.


Duration of storage, if stated.


Where samples will be stored, if stated.


Whether samples will be be coded or identified.


Future Research


 


Identify whether samples or related information may be used
for:


 


Future medical research.


Cancer research.


Disease research.


Biomarker research.


Genetic/genomic research.


Development of new tests.


Development of future medicines.


Other scientific research.


 


Only include purposes specifically described.


 


Optional Research


 


Identify whether:


 


Future research participation is optional.


Separate consent is required.


Participants may decline optional research while remaining
in the main study.


Genetic Testing


 


If applicable, identify:


 


Whether genetic testing will occur.


Purpose of testing.


Whether individual genetic results will be shared.


Any limitations.


Commercial Use


 


Identify whether:


 


Commercial products may result.


Participants will receive payment or ownership rights.


Intellectual property provisions.


 


Include only participant-facing information.


 


Withdrawal of Samples


 


Identify:


 


Whether participants may request destruction of stored
samples.


Circumstances where samples cannot be withdrawn.


Limitations after samples have already been analyzed or
shared.


Mandatory Writing Rules


Distinguish between:


Samples collected for this study, and


Samples stored or used for future research.


Clearly explain whether future research is optional or
mandatory.


Clearly explain whether participants can decline future
research without affecting participation in the main study.


Clearly explain whether stored samples are coded or
otherwise protected.


Clearly explain whether genetic testing will occur, if
applicable.


Clearly explain whether participants will receive individual
research or genetic results, if applicable.


Clearly explain whether commercial products may result and
whether participants will receive financial benefits, if applicable.


Clearly explain any options to withdraw stored samples, if
applicable.


Do not repeat detailed sample collection procedures already
described in Section 3.


Avoid technical laboratory descriptions.


Avoid speculative statements about future research.


Do not assume optional research exists unless explicitly
described.


Study Information


 


[Insert protocol-specific information]


 


Include the Following Questions and Answers


What samples will be collected and how will they be used?


 


Describe:


 


Types of ALL samples collected (briefly, without repeating
collection procedures).


Purpose of using the samples for this study.


Whether any leftover samples will be stored after the study.


Whether samples will be coded or otherwise protected, if
described.


 


Requirements:


 


Focus on how samples will be used rather than how they are
collected.


Do not repeat study procedures already described in Section
3.


Will my samples be used for future research?


 


Describe:


 


Whether samples may be stored.


Possible future research uses.


Genetic or biomarker research, if applicable.


Whether future research is optional or required.


Whether separate consent is required, if applicable.


 


Requirements:


 


Clearly distinguish research for this study from future
research.


Explain participant choices, if applicable.


What choices do I have about future research?


 


Include only if future research is optional.


 


Describe:


 


Whether participation in future research is voluntary.


Whether participants may decline future research and still
participate in the main study.


Whether participants may change their decision later, if
described.


Any limitations on withdrawing stored samples.


 


Requirements:


 


Use balanced, participant-friendly language.


Do not overstate withdrawal rights beyond the source
materials.


Will I receive results from research done on my samples?


 


Include only if applicable.


 


Describe:


 


Whether individual research or genetic results will be
shared.


Why results may or may not be shared.


Whether study findings may contribute to future medical
research.


 


Requirements:


 


Include only information supported by the source materials.


Style Instructions


Use the question headings exactly as written above.


Include only questions that apply to the study.


Keep explanations concise, clear, and easy to understand.


Use short paragraphs and bullet points where appropriate.


Present information in a neutral, transparent, and
participant-centered manner.


Avoid repeating sample collection procedures already
described in Section 3.


Avoid discussing study procedures, risks, benefits, privacy,
costs, compensation, withdrawal from the main study, or signatures.


Integrate any cross-reference naturally into the text rather
than using standalone section references.


Ensure participants can clearly understand:


What happens to their samples,


Whether samples will be stored,


Whether samples may be used for future research,


Whether future research is optional,


What choices they have regarding future research,


Whether they will receive individual research results.`, 
    context: '' 
  },
  { 
    title: '12. If you stop/leave; post‑trial access',
    protocolSources: PROTOCOL_SOURCE_MAPPINGS['12. If you stop/leave; post‑trial access'],
    prompt: `You are an expert clinical-regulatory medical writer drafting an Informed Consent Form (ICF) for a global oncology clinical trial. Write only Section 12: If You Stop or Leave the Study; Post-Trial Access.


Requirements

Use clear, patient-friendly language suitable for approximately an 8th-grade reading level.

Use a question-and-answer format.

Write directly to the participant using "you" and "your."

Use short sentences, plain language, and short paragraphs.

Avoid unnecessary medical, scientific, legal, or regulatory jargon.

If a technical term must be used, explain it in simple language.

Ensure the content aligns with FDA informed consent requirements, ICH E6(R3), CIOMS guidance, and international ethics committee expectations.

Present information in a neutral, factual, transparent, and participant-centered manner.

Use balanced language that supports informed decision-making.

Avoid wording that is coercive, judgmental, promotional, or discouraging.

Include only information explicitly described in the source materials.

Do not invent withdrawal procedures, follow-up requirements, post-trial treatment access, or reasons for study discontinuation.

Do not repeat information already explained in earlier sections unless it is necessary to explain what happens after a participant stops participating.


Source Documents
Use only the following source materials:


Protocol discontinuation and withdrawal sections.

End-of-treatment and follow-up procedures.

Post-trial access provisions.

Protocol safety follow-up requirements.

Investigator's Brochure (if applicable).

Sponsor post-trial treatment policy.

Country-specific informed consent language.

Relevant protocol amendments.

These documents should be treated as the sole source of truth for this section.


Information Extraction Requirements
Before drafting, identify and extract only information explicitly stated regarding:

Participant Withdrawal

Participant's right to stop participating.

Whether participants may stop treatment but continue follow-up.

Whether participants may withdraw completely.

Any requested final study visit.

Whether participants may refuse additional follow-up.


Investigator Withdrawal
Identify situations where the study doctor or sponsor may stop a participant's study participation, such as:


Safety concerns.

Disease progression.

Side effects.

Pregnancy.

Failure to meet study requirements.

Study closure.

Sponsor decision.

Regulatory decision.

Include only reasons explicitly stated.


Follow-up After Withdrawal
Identify:


Safety follow-up visits.

Telephone follow-up.

Survival follow-up.

Long-term follow-up.

Collection of health information after stopping treatment.

Participant choices regarding follow-up.


Post-Trial Access
Identify whether:


Participants may continue receiving study treatment after the study.

Expanded access provisions exist.

Continued access criteria exist.

Alternative treatment arrangements are described.

No post-trial access is planned.

Include only what is explicitly stated.


Mandatory Writing Rules

Clearly explain that participation is voluntary.

Clearly explain that participants may stop participating at any time.

Clearly explain that choosing to stop participating will not affect their regular medical care or legal rights, if stated in the source materials or required by applicable regulations.

Distinguish between:

Stopping study treatment,

Leaving the study completely,

Continuing follow-up after treatment stops.


Clearly explain any requested end-of-study assessments.

Clearly explain any safety follow-up requirements.

Clearly explain circumstances under which the study doctor or sponsor may stop participation.

Clearly explain post-trial access arrangements only if explicitly described.

If no post-trial access is described, state this factually without implying participants are entitled to continued treatment.

Do not imply participants must remain in the study.

Do not imply participants are required to complete follow-up unless explicitly required by the protocol.

Avoid repeating detailed discussions of voluntary participation already presented in earlier sections.

Avoid legalistic language.


Study Information
[Insert protocol-specific information]


Include the Following Questions and Answers
What happens if I decide to stop taking part in the study?
Describe:


Participation is voluntary.

Participants may stop participating at any time.

What participants should tell the study team if they decide to stop.

Whether a final study visit or safety assessment may be requested.

Whether participants may choose whether to continue follow-up, if applicable.

Requirements:


Present this information in a supportive and respectful manner.

Avoid wording that could discourage withdrawal.

Do not begin with "Yes" or "No."


Can the study doctor stop my participation in the study?
Describe:


Circumstances under which the study doctor, sponsor, or regulatory authorities may stop participation.

Examples such as safety concerns, study closure, disease progression, pregnancy, protocol requirements, or sponsor decisions, if applicable.

Requirements:


Include only protocol-supported reasons.

Explain that these decisions are made to protect participant safety, study integrity, or because the study has ended.


What happens after I stop the study?
Describe:


End-of-treatment visit, if applicable.

Safety follow-up.

Long-term follow-up.

Survival follow-up.

Collection of health information after treatment ends, if applicable.

Participant choices regarding follow-up, if applicable.

Requirements:


Include only information explicitly described in the source materials.

Clearly distinguish mandatory and optional follow-up, if applicable.


Will I be able to continue receiving the study treatment after the study ends?
Include only if applicable.
Describe:


Whether continued access to study treatment is available.

Eligibility criteria, if described.

Alternative treatment arrangements, if described.

Situations where continued treatment will not be available.

Requirements:


Use balanced, factual language.

Do not promise continued access unless explicitly supported by the source materials.

If no post-trial access is described in the source materials, state this clearly and factually without creating expectations.


Style Instructions

Use the question headings exactly as written above.

Include only questions that apply to the study.

Keep explanations concise, clear, and easy to understand.

Use short paragraphs and bullet points where appropriate.

Present withdrawal information in a supportive and participant-centered manner.

Present post-trial access information factually and without creating expectations.

Avoid repeating detailed discussions of voluntary participation, study procedures, risks, benefits, privacy, costs, compensation, or signatures.

Integrate any cross-reference naturally into the text rather than using standalone section references.

Ensure participants can clearly understand:

Their right to stop participating,

What happens if they stop treatment,

What follow-up may occur,

When the study doctor may stop participation,

Whether study treatment may continue after the study ends.`, 
    context: '' 
  },
  { 
    title: '13. Oversight, contacts, results, eConsent',
    protocolSources: PROTOCOL_SOURCE_MAPPINGS['13. Oversight, contacts, results, eConsent'],
    prompt: `You are an expert clinical-regulatory medical writer drafting an Informed Consent Form (ICF) for a global oncology clinical trial. Write only Section 13: Oversight, Contacts, and eConsent.


Requirements

Use clear, patient-friendly language suitable for approximately an 8th-grade reading level.

Use a question-and-answer format.

Write directly to the participant using "you" and "your."

Use short sentences, plain language, and short paragraphs.

Avoid unnecessary medical, scientific, legal, or regulatory jargon.

Explain any technical terms in simple language.

Ensure the content aligns with FDA informed consent requirements, ICH E6(R3), CIOMS guidance, and international ethics committee expectations.

Present information in a neutral, factual, transparent, and participant-centered manner.

Avoid wording that is promotional, legalistic, coercive, or discouraging.

Include only information explicitly described in the source materials.

If participant-facing contact details are not available in the source materials, insert clearly labeled placeholders rather than omitting the information or creating fictional contact details.

Do not invent contact names, telephone numbers, email addresses, organizations, or office hours.

This section should focus only on participant support, study oversight, and electronic informed consent (if applicable). Do not repeat information already described in previous sections.


Applicability Determination
Determine whether the following apply:


Study contact information

Emergency contact information

Participant rights contact

Ethics Committee (EC)/Institutional Review Board (IRB)/Independent Ethics Committee (IEC) oversight

Regulatory oversight (participant-facing only)

Electronic informed consent (eConsent)

Include only the applicable questions below.


Source Documents
Use only the following source materials:


Protocol administrative sections

Informed Consent Form administrative language

Site-specific information

Sponsor-provided contact information

Ethics Committee/IRB language

Country-specific informed consent language

eConsent instructions

Relevant protocol amendments

These documents are the sole source of truth for this section.


Information Extraction Requirements
Before drafting, identify and extract all participant-facing administrative information.

Study Team Contacts
Identify:


Principal Investigator or Study Doctor

Research Nurse

Study Coordinator

Clinic or Study Site

Telephone number(s)

Email address(es)

Office hours or availability

If any of these are unavailable, insert placeholders such as:


[Principal Investigator Name]

[Research Nurse Name]

[Study Coordinator Name]

[Study Site Telephone Number]

[Study Team Email Address]

[Office Hours]


Emergency Contacts
Identify:


Emergency contact person or role

24-hour telephone number

Emergency medical instructions

After-hours contact

If unavailable, insert placeholders such as:


[24-hour Emergency Contact Number]

[Emergency Contact Name/Role]


Participant Rights Contacts
Identify:


Ethics Committee

Institutional Review Board

Independent Ethics Committee

Human Research Protection Office

Participant Rights Office

Include:


Contact name (if applicable)

Telephone number

Email address

Postal address (if provided)

If unavailable, insert placeholders such as:


[IRB/IEC Contact Name]

[IRB/IEC Telephone Number]

[IRB/IEC Email Address]

[IRB/IEC Postal Address]


Study Oversight
Identify participant-facing information describing:


Independent ethics review

Ethics Committee approval

Institutional Review Board oversight

Regulatory oversight, if participant-facing


Electronic Informed Consent (eConsent)
Identify:


Whether eConsent is used

Electronic signature process

Opportunity to ask questions before signing

Whether participants receive a copy of the signed consent form

Paper consent alternative, if applicable


Mandatory Writing Rules

Clearly explain whom participants should contact for:

Questions about the study.

Questions about study visits.

Medical concerns.

Side effects.

Study-related injury.

Medical emergencies.

Questions about participant rights.

Complaints about participation, if applicable.


Include the actual participant-facing contact information whenever available.

If contact information is unavailable, insert clearly labeled placeholders rather than omitting the information.

Never create fictional contact details.

Clearly explain that an independent Ethics Committee, Institutional Review Board, or equivalent ethics review body has reviewed and approved the study before it began, where applicable.

Explain the purpose of ethics review in simple language (for example, to help protect the rights, safety, and well-being of participants).

Clearly explain eConsent procedures only if applicable.

Explain that participants will receive a copy of the signed informed consent form (paper or electronic, as applicable).

Avoid repeating information already covered in earlier sections (such as privacy, study results, risks, benefits, procedures, or withdrawal).

Avoid legalistic language.


Study Information
[Insert protocol-specific information]


Include the Following Questions and Answers
Who can I contact if I have questions or need help?
Describe:
Provide separate contacts for:


Questions about the study.

Study appointments.

Medical concerns.

Side effects.

Study-related injury.

Medical emergencies.

Questions about participant rights.

For each contact include, where available:


Name or role

Organization

Telephone number

Email address

Office hours or availability

If any information is unavailable, insert appropriate placeholders.

Requirements

Organize contacts by purpose rather than simply listing names.

Use only information from the source materials.

Never invent contact details.

If contact details are not available, use standardized placeholders so they can be completed before finalization.


Who is overseeing this study?
Describe:


That the study has been reviewed and approved by an independent Ethics Committee (EC), Institutional Review Board (IRB), Independent Ethics Committee (IEC), or equivalent ethics review body, as applicable.

The purpose of independent ethics review.

Regulatory oversight, if participant-facing.

Requirements

Explain oversight using simple, patient-friendly language.

Avoid technical regulatory descriptions.

Include only information supported by the source materials.


How does electronic consent (eConsent) work?
Include only if eConsent is used.
Describe:


How participants review the informed consent electronically.

How participants sign the informed consent electronically.

That participants have the opportunity to ask questions before signing.

That participants will receive a copy of the signed informed consent form.

Whether a paper consent option is available, if applicable.

Requirements

Use simple, reassuring language.

Focus on participant understanding.

Include only information described in the source materials.


Style Instructions

Use the question headings exactly as written above.

Include only questions that apply to the study.

Keep explanations concise, clear, and easy to understand.

Use short paragraphs.

Present multiple contact details in an easy-to-read format (such as a simple table or bulleted list).

Clearly distinguish:

Study Team Contacts

Emergency Contacts

Participant Rights/IRB/EC Contacts

Sponsor Contacts, if participants are instructed to contact the sponsor directly.


Use placeholders whenever participant-facing contact information has not yet been finalized.

Avoid repeating information already covered in earlier sections.

Integrate any cross-reference naturally into the text rather than using standalone section references.

Ensure participants can clearly understand:

Who to contact with questions or concerns.

Who to contact in an emergency.

Who to contact about their rights as a research participant.

Who oversees the study.

How electronic informed consent works, if applicable.`, 
    context: '' 
  },
  { 
    title: '14. Consent & signatures',
    protocolSources: PROTOCOL_SOURCE_MAPPINGS['Consent & signatures'],
    prompt: `You are an expert clinical-regulatory medical writer drafting an Informed Consent Form (ICF) for a global oncology clinical trial. Write only Section 14: Consent and Signatures.
Requirements

Use clear, patient-friendly language suitable for approximately an 8th-grade reading level.

Use simple, respectful, and neutral language.

Write directly to the participant using "you" and "your."

Avoid unnecessary medical, scientific, legal, or regulatory jargon.

Ensure the content aligns with FDA informed consent requirements, ICH E6(R3), CIOMS guidance, and international ethics committee expectations.

This section should document the participant's informed consent and the signatures required to enroll in the study.

Do not repeat information already explained elsewhere in the ICF (such as study procedures, risks, benefits, privacy, costs, alternatives, or withdrawal rights).

Include only participant-facing certification statements and signature blocks.

If site-specific names or information are unavailable, insert clearly labeled placeholders rather than inventing information.


Source Documents
Use only the following source materials:


Sponsor ICF template.

Protocol administrative sections.

Country-specific informed consent requirements.

Ethics Committee/IRB requirements.

Site-specific signature requirements.

eConsent requirements, if applicable.

Relevant protocol amendments.

These documents are the sole source of truth for this section.


Information Extraction Requirements
Before drafting, identify whether the source materials require signature sections for:

Participant

Adult participant.

Legally Authorized Representative (LAR), if applicable.

Parent(s), if applicable.

Guardian, if applicable.


Person Obtaining Consent
Identify whether signatures are required for:


Principal Investigator.

Sub-investigator.

Study physician.

Research nurse.

Study coordinator.

Other delegated study personnel authorized to obtain consent.


Witness
Determine whether a witness signature is required.
Examples may include:


Illiterate participant.

Participant unable to read.

Impartial witness required by local regulations.

Other country-specific requirements.

Include only if required.


Interpreter
Determine whether interpreter certification or signature is required.
Include only if applicable.


Electronic Consent
Determine whether:


Electronic signatures are permitted.

Electronic date/time stamps are required.

Paper signatures remain an option.

Include only if supported by the source materials.


Mandatory Writing Rules

Begin with a brief participant acknowledgment that confirms:


The participant has read (or had read to them) the information in the consent form.

The participant has had the opportunity to ask questions.

Their questions have been answered to their satisfaction.

They understand the information provided.

They voluntarily agree to participate in the study.

They understand they will receive a copy of the signed informed consent form.


Do not require participants to waive any legal rights.

Do not include exculpatory language.

Do not imply that participation is mandatory.

Do not restate detailed study information already described elsewhere in the ICF.

Include only signature blocks required by the source materials.

If names or site-specific information are unavailable, use standardized placeholders.

Use consistent formatting for all signature blocks.

Include signature dates for all required signatories.

Include printed names where applicable.

Include electronic signature fields only if eConsent is used.


Study Information
[Insert protocol-specific information]


Include the Following Sections
Participant Statement
Include a concise statement confirming that:


The participant has read this consent form, or it has been read to them.

The participant has had enough time to consider participation.

The participant has had the opportunity to ask questions.

Their questions have been answered.

The participant understands the information provided.

Participation is voluntary.

The participant agrees to participate in the study.

The participant will receive a copy of the signed informed consent form.

Keep this statement concise and easy to understand.


Participant Signature
Include fields for:


Participant Name (printed)

Participant Signature

Date

Time (if required)

If electronic consent is used, include electronic signature fields only if required by the source materials.


Legally Authorized Representative (if applicable)
Include only if applicable.
Provide:


Brief certification statement.

Printed Name.

Relationship to participant.

Signature.

Date.

Time (if required).


Parent(s) or Legal Guardian (if applicable)
Include only if applicable.
Provide signature blocks required by:


Local regulations.

Study requirements.


Person Obtaining Informed Consent
Provide:


Certification that informed consent discussions were conducted.

Confirmation that questions were answered.

Confirmation that consent was obtained before any study-specific procedures began.

Include fields for:


Printed Name.

Role/Title.

Signature.

Date.

Time (if required).

If names are unavailable, use placeholders such as:


[Principal Investigator Name]

[Delegated Study Team Member Name]


Witness (if applicable)
Include only if required.
Provide:


Brief witness certification.

Printed Name.

Signature.

Date.

Time (if required).


Interpreter (if applicable)
Include only if required.
Provide:


Brief interpreter certification.

Printed Name.

Signature.

Date.

Time (if required).


Style Instructions

Use clear, respectful, participant-friendly language.

Keep certification statements concise.

Avoid lengthy legal language.

Avoid repeating study information.

Clearly separate each signature block using headings.

Maintain consistent formatting throughout.

Use placeholders where site-specific information is unavailable.

Ensure signature blocks are easy to complete in both paper and electronic formats.`, 
    context: '' 
  },
];

// Function to get predefined ICF sections
export const getPredefinedICFSections = async (projectId) => {
  // Return default sections without clinical intake context
  return DEFAULT_ICF_SECTIONS;
}; 